import type { DeploymentEnvironment, JobSchedule } from '@dev-console/types';
import { useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';

import { setJobScheduleEnabled } from '@/config/api-helpers';

interface ToggleScheduleDialogProps {
  schedule: JobSchedule;
  environment: DeploymentEnvironment;
  onClose: () => void;
  onSuccess: (updated: JobSchedule) => void;
  onError: (err: unknown) => void;
}

type DialogState = 'confirm' | 'loading';

export function ToggleScheduleDialog({
  schedule,
  environment,
  onClose,
  onSuccess,
  onError,
}: ToggleScheduleDialogProps) {
  const [state, setState] = useState<DialogState>('confirm');
  const titleId = useId();

  const isDisable = schedule.enabled;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (state === 'loading') return;
      onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, state]);

  const handleConfirm = async () => {
    setState('loading');
    try {
      const response = await setJobScheduleEnabled(environment, {
        scheduleId: schedule.id,
        enabled: !schedule.enabled,
      });
      onSuccess(response.schedule);
    } catch (err) {
      setState('confirm');
      onError(err);
      onClose();
    }
  };

  return createPortal(
    <div
      role='dialog'
      aria-modal='true'
      aria-labelledby={titleId}
      className='fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 p-4'
    >
      <div className='flex max-h-[calc(100vh-2rem)] w-full max-w-lg flex-col rounded-lg bg-white shadow-xl'>
        <div className='border-b border-neutral-200 px-6 py-4'>
          <h3 id={titleId} className='text-lg font-medium text-neutral-900'>
            {isDisable ? 'Disable' : 'Enable'} {environment} schedule
          </h3>
        </div>

        <div className='min-h-0 space-y-4 overflow-y-auto px-6 py-5'>
          {state === 'confirm' && (
            <>
              <div
                className={`rounded-md border p-3 text-sm ${
                  isDisable
                    ? 'border-red-300 bg-red-50 text-red-950'
                    : 'border-neutral-200 bg-neutral-50 text-neutral-700'
                }`}
              >
                {isDisable ? (
                  <p>
                    This stops <span className='font-mono text-xs'>{schedule.jobName}</span> from running on its
                    schedule in {environment}. Runs that are already queued will still happen, and you can re-enable it
                    at any time.
                  </p>
                ) : (
                  <p>
                    This starts running <span className='font-mono text-xs'>{schedule.jobName}</span> on its schedule in{' '}
                    {environment}, beginning at the next scheduled time. Runs missed while it was disabled are skipped.
                  </p>
                )}
              </div>

              <p className='text-sm text-neutral-600'>
                Cron: <span className='font-mono text-xs text-neutral-800'>{schedule.cronExpression}</span> (UTC) · Pod:{' '}
                <span className='text-neutral-800'>{schedule.pod}</span> · Lane:{' '}
                <span className='text-neutral-800'>{schedule.lane}</span>
              </p>
            </>
          )}

          {state === 'loading' && (
            <p className='py-6 text-center text-sm text-neutral-500'>
              {isDisable ? 'Disabling schedule…' : 'Enabling schedule…'}
            </p>
          )}
        </div>

        <div className='flex justify-end gap-2 border-t border-neutral-200 px-6 py-4'>
          {state === 'confirm' && (
            <>
              <button
                type='button'
                onClick={onClose}
                className='rounded-md border border-neutral-200 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50'
              >
                Cancel
              </button>
              <button
                type='button'
                onClick={() => void handleConfirm()}
                className={`rounded-md px-4 py-2 text-sm font-medium text-white ${
                  isDisable ? 'bg-red-600 hover:bg-red-700' : 'bg-neutral-900 hover:bg-neutral-800'
                }`}
              >
                {isDisable ? 'Confirm disable' : 'Confirm enable'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
